import React, {useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import Layout from '../components/Layout';
import {api} from '../../api';

const COLUMNS = [
  {status: 'PENDING_SCHEDULING', label: 'Pending Scheduling', color: 'bg-orange-500', bg: 'bg-orange-50'},
  {status: 'SCHEDULED', label: 'Scheduled', color: 'bg-blue-500', bg: 'bg-blue-50'},
  {status: 'IN_PROGRESS', label: 'In Progress', color: 'bg-yellow-500', bg: 'bg-yellow-50'},
  {status: 'COMPLETED', label: 'Completed', color: 'bg-green-500', bg: 'bg-green-50'},
  {status: 'CANCELLED', label: 'Cancelled', color: 'bg-red-500', bg: 'bg-red-50'},
];

export default function JobBoard() {
  const [jobs, setJobs] = useState([]);
  const [dragJob, setDragJob] = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);

  const load = () => api('/jobs').then(r => r.ok ? r.json() : []).then(setJobs);
  useEffect(() => { load(); }, []);

  const handleDragStart = (e, job) => {
    setDragJob(job);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, status) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverCol(status);
  };

  const handleDrop = async (e, status) => {
    e.preventDefault();
    setDragOverCol(null);
    if (!dragJob || dragJob.status === status) { setDragJob(null); return; }
    setJobs(prev => prev.map(j => j.id === dragJob.id ? {...j, status} : j));
    setDragJob(null);
    await api(`/jobs/${dragJob.id}/status`, {method: 'PUT', body: JSON.stringify({status})});
    load();
  };

  return (
    <Layout>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Job Board</h2>
      <div className="flex gap-4 overflow-x-auto pb-4" style={{minHeight: 'calc(100vh - 180px)'}}>
        {COLUMNS.map(col => {
          const colJobs = jobs.filter(j => j.status === col.status);
          const isOver = dragOverCol === col.status && dragJob?.status !== col.status;
          return (
            <div key={col.status}
              className={`flex-shrink-0 w-72 rounded-lg flex flex-col ${isOver ? 'ring-2 ring-teal-400' : ''}`}
              onDragOver={e => handleDragOver(e, col.status)}
              onDragLeave={() => setDragOverCol(null)}
              onDrop={e => handleDrop(e, col.status)}>
              <div className={`flex items-center gap-2 px-4 py-3 rounded-t-lg ${col.bg}`}>
                <div className={`w-3 h-3 rounded-full ${col.color}`} />
                <span className="text-sm font-semibold text-gray-800">{col.label}</span>
                <span className="text-xs text-gray-500 ml-auto">{colJobs.length}</span>
              </div>
              <div className={`flex-1 p-2 space-y-2 ${col.bg} bg-opacity-30 rounded-b-lg min-h-[100px]`}>
                {colJobs.map(job => (
                  <div key={job.id} draggable onDragStart={e => handleDragStart(e, job)}
                    className={`bg-white rounded-lg shadow-sm border p-3 cursor-grab active:cursor-grabbing hover:shadow-md transition-shadow ${dragJob?.id === job.id ? 'opacity-40' : ''}`}>
                    <Link to={`/jobs/${job.id}`} className="text-sm font-medium text-teal-600 hover:text-teal-800 block">{job.title}</Link>
                    <p className="text-xs text-gray-500 mt-1">{job.jobNumber}</p>
                    {(job.companyName || job.contactName) && (
                      <p className="text-xs text-gray-600 mt-1 truncate">{job.companyName || job.contactName}</p>
                    )}
                    {job.scheduledStart && (
                      <p className="text-xs text-gray-400 mt-1">📅 {new Date(job.scheduledStart + 'T00:00:00').toLocaleDateString()}</p>
                    )}
                    {job.assignments?.length > 0 && (
                      <div className="flex items-center gap-1 mt-2">
                        {job.assignments.slice(0, 3).map(a => (
                          <span key={a.id} className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-teal-100 text-teal-700 text-xs font-medium" title={a.employeeName}>
                            {a.employeeName?.charAt(0)}
                          </span>
                        ))}
                        {job.assignments.length > 3 && <span className="text-xs text-gray-400">+{job.assignments.length - 3}</span>}
                      </div>
                    )}
                    {job.total > 0 && <p className="text-xs font-medium text-gray-700 mt-1">${job.total.toFixed(2)}</p>}
                  </div>
                ))}
                {colJobs.length === 0 && !isOver && (
                  <div className="text-center py-8 text-gray-400 text-xs">No jobs</div>
                )}
                {isOver && (
                  <div className="border-2 border-dashed border-teal-300 rounded-lg py-4 text-center text-xs text-teal-500">
                    Drop here
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Layout>
  );
}
